import React, { useEffect, useMemo, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { countryToIso2 } from '../utils/countryFlag';

type Props = {
  /** Optional default country code (iso2), e.g. "gb". Falls back to user based country. */
  defaultCountry?: string;
  compact?: boolean;
};

const SmsPhoneVerification: React.FC<Props> = ({ defaultCountry, compact = false }) => {
  const { user, refreshUser } = useAuth();
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState<'idle' | 'send' | 'verify' | 'save'>('idle');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Prefer based country from signup/profile over login IP / phone-input defaults.
  const resolvedDefaultCountry = useMemo(() => {
    if (defaultCountry) return defaultCountry.toLowerCase();
    const iso2 = countryToIso2(user?.country || '');
    return iso2 ? iso2.toLowerCase() : 'us';
  }, [defaultCountry, user?.country]);

  useEffect(() => {
    if (!user) return;
    setSmsEnabled(Boolean(user.notificationPreferences?.smsNotifications));
    setPhone(user.phone || '');
    setPhoneVerified(Boolean(user.phoneVerified));
    if (user.phoneVerified && user.phone) {
      setCodeSent(false);
    }
  }, [user]);

  const formatPhone = (value: string) => (value.startsWith('+') ? value : `+${value}`);

  const clearMessages = () => {
    setError('');
    setInfo('');
  };

  const handleSendCode = async () => {
    clearMessages();
    if (!phone || phone.length < 8) {
      setError('Enter a valid phone number with country code.');
      return;
    }
    try {
      setBusy('send');
      const send = httpsCallable(functions, 'sendPhoneVerificationCode');
      const result = await send({ phone: formatPhone(phone) });
      const data = result.data as { success: boolean; message?: string };
      setCodeSent(true);
      setPhoneVerified(false);
      setInfo(data.message || 'Verification code sent. Check your phone.');
      setCode('');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to send code.';
      setError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setBusy('idle');
    }
  };

  const handleVerifyCode = async () => {
    clearMessages();
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the 6-digit code from the SMS.');
      return;
    }
    try {
      setBusy('verify');
      const verify = httpsCallable(functions, 'verifyPhoneCode');
      const result = await verify({ code: code.trim() });
      const data = result.data as { success: boolean; message?: string; phone?: string };
      setPhoneVerified(true);
      setSmsEnabled(true);
      setCodeSent(false);
      setCode('');
      if (data.phone) setPhone(data.phone.replace(/^\+/, ''));
      setInfo(data.message || 'Phone verified. SMS alerts enabled.');
      await refreshUser?.();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Verification failed.';
      setError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setBusy('idle');
    }
  };

  const handleToggleSms = async (enabled: boolean) => {
    clearMessages();
    if (enabled && !phoneVerified) {
      setSmsEnabled(true); // show phone UI; must verify before it sticks
      setInfo('Enter your number and verify with the SMS code to enable alerts.');
      return;
    }
    try {
      setBusy('save');
      const update = httpsCallable(functions, 'updateNotificationPreferences');
      const result = await update({
        smsNotifications: enabled,
        phone: enabled ? formatPhone(phone) : undefined,
      });
      const data = result.data as { success: boolean; message?: string };
      setSmsEnabled(enabled);
      setInfo(data.message || 'Preferences saved.');
      await refreshUser?.();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to save preferences.';
      setError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\(.*\)$/, ''));
      setSmsEnabled(!enabled);
    } finally {
      setBusy('idle');
    }
  };

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <label className="flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={smsEnabled}
          onChange={e => handleToggleSms(e.target.checked)}
          className="sr-only"
          disabled={busy !== 'idle'}
        />
        <div
          className="w-6 h-6 border-2 rounded flex items-center justify-center transition-all mr-3 flex-shrink-0"
          style={{
            borderColor: '#92d050',
            backgroundColor: smsEnabled ? '#92d050' : 'transparent',
          }}
        >
          {smsEnabled && (
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>
        <span className="text-white text-sm sm:text-base">
          {user?.userType === 'startup' ? (
            <>
              Add SMS alerts to hear from interested investors{' '}
              <span className="font-semibold">faster</span>.
            </>
          ) : (
            'SMS alerts (faster)'
          )}
        </span>
        {phoneVerified && smsEnabled ? (
          <span className="ml-2 text-xs text-emerald-400 font-medium">Verified</span>
        ) : null}
      </label>
      <div className="ml-9">
        <p className="text-gray-400 text-sm">
          {user?.userType === 'startup'
            ? 'Get a text as soon as an investor wants to connect.'
            : 'Be immediately notified when a new startup matches your preferences.'}
        </p>
        <p className="text-gray-400 text-sm">Pause anytime</p>
      </div>

      {smsEnabled && (
        <div className="ml-0 sm:ml-9 space-y-3 text-left">
          <PhoneInput
            country={resolvedDefaultCountry}
            value={phone}
            onChange={val => {
              setPhone(val);
              if (phoneVerified) {
                setPhoneVerified(false);
                setCodeSent(false);
              }
              clearMessages();
            }}
            countryCodeEditable={false}
            enableLongNumbers
            inputStyle={{ color: 'black', width: '100%', borderRadius: '0.25rem' }}
            dropdownStyle={{ color: 'black', textAlign: 'left' }}
          />

          {!phoneVerified && (
            <>
              <button
                type="button"
                onClick={handleSendCode}
                disabled={busy !== 'idle'}
                className="w-full px-4 py-3 rounded-lg bg-black text-white text-sm font-semibold border border-gray-600 hover:bg-gray-900 disabled:opacity-50"
              >
                {busy === 'send' ? 'Sending…' : 'Send Verification Code'}
              </button>

              <p className="text-xs text-gray-400 text-left">
                Didn&apos;t receive an SMS?{' '}
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={busy !== 'idle'}
                  className="underline hover:text-gray-300 disabled:opacity-50"
                >
                  Click here
                </button>
              </p>

              {codeSent && (
                <div className="space-y-2">
                  <label className="block text-sm text-white" htmlFor="sms-otp-code">
                    Enter the 6-digit code
                  </label>
                  <input
                    id="sms-otp-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full max-w-[200px] rounded-md px-3 py-2 text-gray-900 text-lg tracking-widest"
                    placeholder="••••••"
                  />
                  <button
                    type="button"
                    onClick={handleVerifyCode}
                    disabled={busy !== 'idle' || code.length !== 6}
                    className="block px-4 py-2 rounded-xl border-2 border-emerald-500 text-white font-bold text-sm disabled:opacity-50"
                  >
                    {busy === 'verify' ? 'Verifying…' : 'Verify code'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {error ? <p className="text-red-400 text-sm">{error}</p> : null}
      {info ? <p className="text-emerald-400 text-sm">{info}</p> : null}
    </div>
  );
};

export default SmsPhoneVerification;
